import React, { useState } from 'react';
import {
  Clipboard,
  Nutrition as NutritionIcon,
  ReadBook,
  Link as LinkIcon,
  FileText,
  FactoryBuilding,
  Picture,
  CloseSmall,
  CheckOne
} from '@icon-park/react';
import './styles/TemplateSelector.css';

/**
 * 模板选择器组件
 * 让用户可以选择一个或多个模板来快速创建标签
 */
function TemplateSelector({ isOpen, onClose, onSelect }) {
  const [selectedTemplates, setSelectedTemplates] = useState([]);

  // 模板配置（包含图标和描述）
  const templateConfigs = [
    {
      type: 'blue-white-template',
      name: '蓝色-白色模板',
      icon: Clipboard,
      description: '现代化的蓝色标题栏+白色卡片样式，支持中英文双语标题，专业的视觉效果',
      color: '#1e90ff',
      featured: true,
      isComposite: true, // 标记为组合模板
      components: ['image', 'product-basic', 'nutrition', 'usage', 'inspection', 'related-links'], // 包含的组件
      variant: 'blue-white' // 模板变体标识
    },
    {
      type: 'standard-food-label',
      name: '标准食品标签（浅棕）',
      icon: Clipboard,
      description: '浅棕主题，包含产品图/基础信息/营养成分/食用方法/检验报告/伙伴链接，匹配你提供的截图风格',
      color: '#D4BFA0',
      featured: true,
      isComposite: true, // 标记为组合模板
      components: ['image', 'product-basic', 'nutrition', 'usage', 'inspection', 'related-links'], // 包含的组件
      variant: 'brown-v1' // 浅棕主题变体（截图）
    },
    {
      type: 'product-basic',
      name: '产品基础信息',
      icon: Clipboard,
      description: '包含产品名称、配料、净含量、生产信息等基础信息',
      color: '#2563EB'
    },
    {
      type: 'nutrition',
      name: '营养成分表',
      icon: NutritionIcon,
      description: '符合GB 28050标准的营养成分表模板',
      color: '#10B981'
    },
    {
      type: 'usage',
      name: '食用方法',
      icon: ReadBook,
      description: '展示产品食用方法和注意事项',
      color: '#F59E0B'
    },
    {
      type: 'related-links',
      name: '相关链接',
      icon: LinkIcon,
      description: '添加产品相关网站和资源链接',
      color: '#8B5CF6'
    },
    {
      type: 'inspection',
      name: '检验报告',
      icon: FileText,
      description: '上传检验报告和标准文本',
      color: '#EF4444'
    },
    {
      type: 'gds-traceability',
      name: 'GDS追溯模板',
      icon: FactoryBuilding,
      description: 'GDS追溯系统专用模板，包含完整的追溯信息',
      color: '#4CAF50',
      featured: true
    }
  ];

  const handleToggleTemplate = (templateType) => {
    console.log('🎯 [TemplateSelector] 用户点击模板:', templateType);
    const template = templateConfigs.find(t => t.type === templateType);
    console.log('📋 [TemplateSelector] 找到模板配置:', {
      type: template?.type,
      name: template?.name,
      isComposite: template?.isComposite,
      variant: template?.variant,
      components: template?.components
    });
    
    // 如果是组合模板（标准食品标签或蓝色-白色模板），单独处理
    if (template?.isComposite) {
      console.log('🔵 [TemplateSelector] 这是组合模板，执行组合模板选择逻辑');
      setSelectedTemplates(prev => {
        console.log('📝 [TemplateSelector] 当前已选择的模板:', prev);
        if (prev.includes(templateType)) {
          // 取消选择：移除组合模板
          console.log('❌ [TemplateSelector] 取消选择组合模板:', templateType);
          const newSelection = prev.filter(t => t !== templateType);
          console.log('✅ [TemplateSelector] 取消选择后的模板列表:', newSelection);
          return newSelection;
        } else {
          // 选择组合模板：只保留组合模板，移除其他单个模板和组合模板
          console.log('✅ [TemplateSelector] 选择组合模板:', templateType);
          const filtered = prev.filter(t => {
            const tpl = templateConfigs.find(t2 => t2.type === t);
            return !tpl?.isComposite; // 移除所有组合模板
          });
          console.log('🔍 [TemplateSelector] 移除组合模板后剩余:', filtered);
          const newSelection = filtered.concat([templateType]); // 添加新选择的组合模板
          console.log('✅ [TemplateSelector] 选择组合模板后的最终列表:', newSelection);
          return newSelection;
        }
      });
    } else {
      // 单个模板的处理
      console.log('🔘 [TemplateSelector] 这是单个模板，执行单个模板选择逻辑');
      setSelectedTemplates(prev => {
        console.log('📝 [TemplateSelector] 当前已选择的模板:', prev);
        // 如果选择了组合模板，先移除它
        const hasComposite = prev.some(t => {
          const tpl = templateConfigs.find(t2 => t2.type === t);
          return tpl?.isComposite;
        });
        console.log('🔍 [TemplateSelector] 是否包含组合模板:', hasComposite);
        const newTemplates = hasComposite ? prev.filter(t => {
          const tpl = templateConfigs.find(t2 => t2.type === t);
          return !tpl?.isComposite;
        }) : prev;
        console.log('🧹 [TemplateSelector] 清理组合模板后的列表:', newTemplates);
        
        if (newTemplates.includes(templateType)) {
          console.log('❌ [TemplateSelector] 取消选择单个模板:', templateType);
          const result = newTemplates.filter(t => t !== templateType);
          console.log('✅ [TemplateSelector] 取消选择后的列表:', result);
          return result;
        } else {
          console.log('✅ [TemplateSelector] 添加单个模板:', templateType);
          const result = [...newTemplates, templateType];
          console.log('✅ [TemplateSelector] 添加后的列表:', result);
          return result;
        }
      });
    }
  };

  const handleConfirm = () => {
    console.log('🚀 [TemplateSelector] 用户点击"开始创建"按钮');
    console.log('📋 [TemplateSelector] 当前选中的模板列表:', selectedTemplates);
    
    if (selectedTemplates.length > 0) {
      // 如果选择了组合模板，返回其包含的所有组件和模板变体
      const finalTemplates = [];
      let templateVariant = 'default'; // 默认模板变体
      
      // 优先查找组合模板（应该只有一个）
      const compositeTemplate = selectedTemplates.find(templateType => {
        const template = templateConfigs.find(t => t.type === templateType);
        return template?.isComposite;
      });
      
      console.log('🔍 [TemplateSelector] 查找组合模板结果:', compositeTemplate);
      
      if (compositeTemplate) {
        const template = templateConfigs.find(t => t.type === compositeTemplate);
        console.log('📦 [TemplateSelector] 找到组合模板配置:', {
          type: template?.type,
          name: template?.name,
          variant: template?.variant,
          components: template?.components
        });
        
        if (template?.components) {
          // 组合模板：添加所有组件
          const seq = ['background', ...template.components];
          console.log('📝 [TemplateSelector] 组件序列（包含background）:', seq);
          
          // 蓝白模板不插入分隔线，更贴近目标样式；浅棕模板插入分隔线
          const insertDivider = template.variant === 'brown-v1';
          console.log('🔀 [TemplateSelector] 是否插入分隔线:', insertDivider, '(variant:', template.variant, ')');
          
          for (let i = 0; i < seq.length; i++) {
            finalTemplates.push(seq[i]);
            if (insertDivider && i < seq.length - 1) {
              finalTemplates.push('divider');
              console.log('➖ [TemplateSelector] 在第', i, '个组件后插入分隔线');
            }
          }
          
          // 使用组合模板的变体
          if (template.variant) {
            templateVariant = template.variant;
            console.log('🎨 [TemplateSelector] 设置模板变体:', templateVariant);
          }
        }
      } else {
        // 没有组合模板，只添加单个模板
        console.log('🔘 [TemplateSelector] 没有组合模板，使用单个模板列表');
        selectedTemplates.forEach(templateType => {
          finalTemplates.push(templateType);
          console.log('➕ [TemplateSelector] 添加单个模板:', templateType);
        });
      }
      
      console.log('✅ [TemplateSelector] 最终生成的组件列表:', finalTemplates);
      console.log('🎨 [TemplateSelector] 最终模板变体:', templateVariant);
      
      const selectionResult = {
        components: finalTemplates,
        variant: templateVariant
      };
      console.log('📤 [TemplateSelector] 准备返回选择结果:', selectionResult);
      
      // 返回一个对象，包含组件列表和模板变体
      onSelect(selectionResult);
      console.log('✅ [TemplateSelector] 已调用onSelect回调');
      handleClose();
    } else {
      console.warn('⚠️ [TemplateSelector] 没有选中任何模板，无法创建');
    }
  };

  const handleClose = () => {
    setSelectedTemplates([]);
    onClose();
  };

  const handleCreateBlank = () => {
    // 空白标签也返回对象格式以保持一致性
    onSelect({
      components: [],
      variant: 'default'
    });
    handleClose();
  };

  if (!isOpen) return null;

  return (
    <div className="template-selector-overlay" onClick={handleClose}>
      <div className="template-selector-modal" onClick={(e) => e.stopPropagation()}>
        <button className="template-selector-close" onClick={handleClose}>
          <CloseSmall theme="outline" size={20} />
        </button>

        <div className="template-selector-header">
          <h2 className="template-selector-title">选择模板创建标签</h2>
          <p className="template-selector-subtitle">
            选择一个或多个模板快速开始，也可以创建空白标签
          </p>
        </div>

        <div className="template-selector-content">
          <div className="template-grid">
            {templateConfigs.map((template) => {
              const Icon = template.icon;
              const isSelected = selectedTemplates.includes(template.type);
              
              return (
                <div
                  key={template.type}
                  className={`template-card ${isSelected ? 'selected' : ''} ${template.featured ? 'featured' : ''}`}
                  onClick={() => handleToggleTemplate(template.type)}
                >
                  <div className="template-card-checkbox">
                    {isSelected && (
                      <CheckOne theme="filled" size={20} fill="#fff" />
                    )}
                  </div>
                  <div className="template-card-icon" style={{ backgroundColor: `${template.color}15` }}>
                    <Icon 
                      theme={template.featured ? "filled" : "outline"} 
                      size={32} 
                      fill={template.color} 
                      strokeWidth={3} 
                    />
                  </div>
                  <div className="template-card-content">
                    <h3 className="template-card-name">{template.name}</h3>
                    <p className="template-card-description">{template.description}</p>
                  </div>
                  {template.featured && (
                    <div className="template-card-badge">推荐</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="template-selector-footer">
          <button 
            className="template-selector-btn template-selector-btn-secondary"
            onClick={handleCreateBlank}
          >
            创建空白标签
          </button>
          <button 
            className={`template-selector-btn template-selector-btn-primary ${selectedTemplates.length === 0 ? 'disabled' : ''}`}
            onClick={handleConfirm}
            disabled={selectedTemplates.length === 0}
          >
            {(() => {
              // 计算实际会添加的组件数量
              let componentCount = 0;
              selectedTemplates.forEach(templateType => {
                const template = templateConfigs.find(t => t.type === templateType);
                if (template?.isComposite && template.components) {
                  componentCount += template.components.length;
                } else {
                  componentCount += 1;
                }
              });
              return `开始创建${componentCount > 0 ? ` (${componentCount}个组件)` : ''}`;
            })()}
          </button>
        </div>
      </div>
    </div>
  );
}

export default TemplateSelector;

